import styled from '@emotion/styled';
import { tokens, Text, Icon } from '@hotjar/hotsauce';

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PostTitle = styled(Text)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const SideImage = styled.div<{ url: string }>`
  display: flex;
  flex: 0 0 150px;
  height: 100%;
  border-left: 1px solid ${tokens.color.ui_border};
  background-color: ${tokens.color.ui_background};
  background-image: url(${({ url }) => url});
  background-size: cover;
  background-position: center center;
`;

export const LinkIcon = styled(Icon)`
  position: absolute;
  right: ${tokens.spacer.x1pt5};
  top: 50%;
  transform: translateY(-50%);
`;

const Wrapper = styled.a<{ expanded?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  background-color: ${tokens.color.ui_background};
  margin-bottom: ${tokens.spacer.x2};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.spacer.x1};
  height: 120px;
  overflow: hidden;

  ${TextContent} {
    padding: ${tokens.spacer.x2};
  }

  ${LinkIcon} {
    display: none;
  }

  &:hover {
    background-color: ${tokens.color.bg_hover};
    text-decoration: none;
  }

  ${({ expanded }) =>
    !expanded &&
    `
    height: 68px;

    ${TextContent} {
      padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
    }

    ${SideImage} {
      display: none;
    }

    ${PostTitle} {
      -webkit-line-clamp: 1;
    }

    ${LinkIcon} {
      display: block;
    }
  `}

  &:last-of-type {
    margin-bottom: 0;
  }
`;

interface ArticleLinkProps {
  onClick: () => void;
  href: string;
  target: '_blank' | '_self';
  className?: string;
  wrapperLabel: string;
  imageUrl?: string;
  title: string;
  caption: string;
  expanded?: boolean;
}

export const ArticleLink = ({
  onClick,
  href,
  target = '_blank',
  className,
  wrapperLabel,
  imageUrl,
  title,
  caption,
  expanded,
}: ArticleLinkProps): JSX.Element => {
  return (
    <Wrapper
      expanded={expanded}
      className={className}
      onClick={onClick}
      href={href}
      target={target}
      aria-label={wrapperLabel}
    >
      <TextContent>
        <Text css={{ marginBottom: tokens.spacer.x0pt5 }} textType="caption" textColor="text_light">
          {caption}
        </Text>
        <PostTitle textType="subtitle1" textColor="text_default">
          {title}
        </PostTitle>
      </TextContent>
      {!!imageUrl && expanded ? (
        <SideImage data-testid="side-img" role="img" url={imageUrl} />
      ) : null}
      {target === '_blank' && !expanded ? (
        <LinkIcon color="text_default" icon="open_new_window" description="link icon" />
      ) : null}
    </Wrapper>
  );
};
